import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Tabs, Select, message, Tag } from 'antd';
import api from '../../app/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useTranslation } from '../../shared/hooks/useTranslation';
import { getBcpLocale } from '../../shared/dateFormat';
import style from './ClientsAnalytics.module.scss';

const { TabPane } = Tabs;
const { Option } = Select;

interface ClientStats {
  total_clients: number;
  appointment_clients: number;
  iin_only_clients: number;
  conversion_rate: number;
}

interface AppointmentStats {
  total_appointments: number;
  popular_doctors: Array<{
    doctor_id: string;
    appointment_count: number;
    unique_clients: number;
  }>;
  popular_services: Array<{
    service_id: string;
    appointment_count: number;
    unique_clients: number;
  }>;
  recent_appointments: number;
  last_30_days: number;
}

interface Client {
  id: number;
  iin: string;
  full_name: string;
  phone?: string;
  status: 'iin_entered' | 'appointment_created';
  language: string;
  created_at: string;
}

interface Appointment {
  id: number;
  client_id: number;
  doctor_id: string;
  service_id: string;
  department_id: string;
  appointment_date: string;
  created_at: string;
  client: Client;
}

export function ClientsAnalytics() {
  const [clientStats, setClientStats] = useState<ClientStats | null>(null);
  const [appointmentStats, setAppointmentStats] = useState<AppointmentStats | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { t, language } = useTranslation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const clientStatsResponse = await api.get('/admin/clients/stats');
      setClientStats(clientStatsResponse.data.data);

      const appointmentStatsResponse = await api.get('/admin/appointments/stats');
      setAppointmentStats(appointmentStatsResponse.data.data);

      const clientsResponse = await api.get(`/admin/clients?status=${statusFilter === 'all' ? '' : statusFilter}&limit=1000`);
      setClients(clientsResponse.data.data);

      const appointmentsResponse = await api.get('/admin/appointments?limit=50');
      setAppointments(appointmentsResponse.data.data);
    } catch (error: any) {
      message.error(`${t.clientsErrorLoad}: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t.clientsErrorLoad]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const bcpLocale = getBcpLocale(language);

  const languageLabel = useCallback((lang: string): string => {
    if (lang === 'ru') return t.langLabelRu;
    if (lang === 'kk') return t.langLabelKk;
    if (lang === 'en') return t.langLabelEn;
    return lang;
  }, [t.langLabelRu, t.langLabelKk, t.langLabelEn]);

  const clientColumns = useMemo(() => [
    { title: t.clientsColIin, dataIndex: 'iin', key: 'iin', width: 120 },
    { title: t.clientsColName, dataIndex: 'full_name', key: 'full_name' },
    {
      title: t.clientsColPhone,
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: t.clientsColStatus,
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'appointment_created' ? 'green' : 'orange'}>
          {status === 'appointment_created' ? t.clientsStatusBooked : t.clientsStatusIinOnly}
        </Tag>
      ),
    },
    {
      title: t.clientsColLang,
      dataIndex: 'language',
      key: 'language',
      render: (lang: string) => languageLabel(lang),
    },
    {
      title: t.clientsColCreated,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(bcpLocale),
    },
  ], [t.clientsColIin, t.clientsColName, t.clientsColPhone, t.clientsColStatus,
      t.clientsColLang, t.clientsColCreated, t.clientsStatusBooked, t.clientsStatusIinOnly,
      languageLabel, bcpLocale]);

  const appointmentColumns = useMemo(() => [
    { title: t.clientsColApptId, dataIndex: 'id', key: 'id', width: 80 },
    {
      title: t.clientsColApptClient,
      key: 'client',
      render: (record: Appointment) => (
        <div>
          <div>{record.client.full_name}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{record.client.iin}</div>
        </div>
      ),
    },
    { title: t.clientsColApptDoctor, dataIndex: 'doctor_id', key: 'doctor_id' },
    { title: t.clientsColApptService, dataIndex: 'service_id', key: 'service_id' },
    {
      title: t.clientsColApptDate,
      dataIndex: 'appointment_date',
      key: 'appointment_date',
      render: (date: string) => new Date(date).toLocaleString(bcpLocale),
    },
    {
      title: t.clientsColApptCreated,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(bcpLocale),
    },
  ], [t.clientsColApptId, t.clientsColApptClient, t.clientsColApptDoctor,
      t.clientsColApptService, t.clientsColApptDate, t.clientsColApptCreated, bcpLocale]);

  return (
    <AdminLayout>
      <div className={style.container}>
        <h1>{t.clientsTitle}</h1>

        <Row gutter={[10, 10]} align="stretch" style={{ marginBottom: 12 }}>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatTotal} value={clientStats?.total_clients || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatBooked} value={clientStats?.appointment_clients || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatIinOnly} value={clientStats?.iin_only_clients || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic
                title={t.clientsStatConversion}
                value={clientStats?.conversion_rate || 0}
                suffix="%"
                valueStyle={{ color: 'var(--text-primary)' }}
                precision={1}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatTotalAppts} value={appointmentStats?.total_appointments || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatLast30} value={appointmentStats?.last_30_days || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatPopDoctors} value={appointmentStats?.popular_doctors?.length || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card>
              <Statistic title={t.clientsStatPopServices} value={appointmentStats?.popular_services?.length || 0} valueStyle={{ color: 'var(--text-primary)' }} />
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="clients">
          <TabPane tab={t.clientsTabClients} key="clients">
            <div className={style.filterRow}>
              <span className={style.filterLabel}>{t.clientsStatusFilter}</span>
              <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 200 }}>
                <Option value="all">{t.clientsFilterAll}</Option>
                <Option value="iin_entered">{t.clientsFilterIin}</Option>
                <Option value="appointment_created">{t.clientsFilterBooked}</Option>
              </Select>
            </div>
            <Table
              columns={clientColumns}
              dataSource={clients}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 20 }}
              scroll={{ x: 800 }}
            />
          </TabPane>

          <TabPane tab={t.clientsTabAppointments} key="appointments">
            <Table
              columns={appointmentColumns}
              dataSource={appointments}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 20 }}
              scroll={{ x: 1000 }}
            />
          </TabPane>

          <TabPane tab={t.clientsTabPopDoctors} key="doctors">
            <Table
              columns={[
                { title: t.clientsColDoctorId, dataIndex: 'doctor_id', key: 'doctor_id' },
                { title: t.clientsColApptCount, dataIndex: 'appointment_count', key: 'appointment_count' },
                { title: t.clientsColUnique, dataIndex: 'unique_clients', key: 'unique_clients' },
              ]}
              dataSource={appointmentStats?.popular_doctors || []}
              rowKey="doctor_id"
              loading={loading}
              pagination={false}
            />
          </TabPane>

          <TabPane tab={t.clientsTabPopServices} key="services">
            <Table
              columns={[
                { title: t.clientsColServiceId, dataIndex: 'service_id', key: 'service_id' },
                { title: t.clientsColApptCount, dataIndex: 'appointment_count', key: 'appointment_count' },
                { title: t.clientsColUnique, dataIndex: 'unique_clients', key: 'unique_clients' },
              ]}
              dataSource={appointmentStats?.popular_services || []}
              rowKey="service_id"
              loading={loading}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
